import { Fragment, useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useSystemTemplatePreviewMutation from 'components/domain/automation/systemTemplates/useSystemTemplatePreviewMutation';
import CheckboxField from 'components/forms/checkboxField/CheckboxField';
import DisplayField from 'components/forms/displayField/DisplayField';
import RichTextEditorField from 'components/forms/richTextEditor/RichTextEditorField';
import TextField from 'components/forms/textField/TextField';
import { LanguageCode } from 'i18n/internationalization.types';
import { SystemTemplate } from 'models/automation/SystemTemplates';
import { UsageType } from 'models/automation/Common';
import TemplateModalLanguageSelectField from 'pages/automations/common/TemplateModalLanguageSelectField';
import InvalidLanguageAlert from 'pages/automations/common/invalidLanguageAlert/InvalidLanguageAlert';
import { SystemTemplateEditModalFormValues } from './SystemTemplateEditModal.schema';
import useSystemTemplateToolbarSettings from './fields/useSystemTemplateToolbarSettings';

interface SystemTemplateEditModalFormElementsProps {
  availableLanguages: LanguageCode[];
  systemTemplate: SystemTemplate;
}

const SystemTemplateEditModalFormElements = ({
  availableLanguages,
  systemTemplate,
}: SystemTemplateEditModalFormElementsProps) => {
  const { t } = useTranslation();
  const [isOnPreviewMode, setIsOnPreviewMode] = useState(false);
  const { control } = useFormContext<SystemTemplateEditModalFormValues>();
  // Untyped on purpose: `locales.${language}.content` field paths are built dynamically, which
  // doesn't match react-hook-form's strict `Path<T>` typing for the schema-typed form context.
  const { getValues, setValue } = useFormContext();
  const currentSelectedLanguage = useWatch({ control, name: 'language' });
  const previousLanguageRef = useRef(currentSelectedLanguage);

  // Only the active language's rich text editor is mounted (see render below). Since its
  // `contents` prop only reflects the value at mount time (changing it while mounted would reset
  // the cursor on every keystroke — see the comment in RichTextEditorField), we snapshot the
  // language being left into its stable `content` field so re-selecting it later remounts the
  // editor with the latest edits instead of the original content.
  useEffect(() => {
    const previousLanguage = previousLanguageRef.current;
    previousLanguageRef.current = currentSelectedLanguage;

    if (previousLanguage === currentSelectedLanguage) {
      return;
    }

    setValue(
      `locales.${previousLanguage}.content`,
      getValues(`locales.${previousLanguage}.content_htmlContent`),
    );
  }, [currentSelectedLanguage, getValues, setValue]);

  const {
    data: previewData,
    mutateAsync: fetchPreview,
    isLoading: isLoadingPreview,
  } = useSystemTemplatePreviewMutation();

  const { toolbarSettings, isLoadingVariables } =
    useSystemTemplateToolbarSettings({
      key: systemTemplate.key,
      loadPreview: async (content) => {
        const { data } = await fetchPreview({ htmlContent: content });

        return data.previewContent;
      },
      onEnterPreviewMode: () => setIsOnPreviewMode(true),
      onExitPreviewMode: () => setIsOnPreviewMode(false),
    });

  const { key } = systemTemplate;

  return (
    <>
      <DisplayField
        colSmInput={8}
        colSmLabel={4}
        colXsInput={8}
        colXsLabel={4}
        id="templateName"
        label={t('pageAutomations.systemTemplates.modal.templateName')}
        value={t('pageAutomations.systemTemplates.systemTemplateName', {
          context: key,
        })}
      />
      <DisplayField
        colSmInput={8}
        colSmLabel={4}
        colXsInput={8}
        colXsLabel={4}
        id="templateDescription"
        label={t('pageAutomations.systemTemplates.modal.templateDescription')}
        value={t('pageAutomations.systemTemplates.systemTemplateDescription', {
          context: key,
        })}
      />

      <TemplateModalLanguageSelectField usageType={UsageType.SYSTEM} />

      {isOnPreviewMode && (
        <TextField
          colSmInput={8}
          colSmLabel={4}
          disabled={isOnPreviewMode}
          isLoading={isLoadingPreview}
          label={t('common.subject')}
          name="subjectPreview"
          value={
            previewData?.data.previewTitle ??
            t('form.editor.preview.errorMessage')
          }
        />
      )}

      {availableLanguages.map((language) =>
        language && language === currentSelectedLanguage ? (
          <Fragment key={language}>
            {!isOnPreviewMode && (
              <TextField
                colSmInput={8}
                colSmLabel={4}
                disabled={isOnPreviewMode}
                label={t('common.subject')}
                name={`locales.${language}.subject`}
              />
            )}

            <RichTextEditorField
              colSmInput={12}
              colSmLabel={0}
              isLoading={isLoadingVariables}
              linkMode="edit"
              name={`locales.${language}.content`}
              toolbarSettings={toolbarSettings}
            />
          </Fragment>
        ) : null,
      )}

      <CheckboxField name="sendCopyToAgency">
        {t('pageAutomations.systemTemplates.modal.sendCopyToAgency')}
      </CheckboxField>

      <InvalidLanguageAlert i18nKey="pageAutomations.systemTemplates.modal.invalidLocaleError" />
    </>
  );
};

export default SystemTemplateEditModalFormElements;
